import React from 'react'
import { Helmet } from 'react-helmet'
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Functional & Finance',
    items: [
      { name: 'SAP S/4HANA', icon: '🔄' },
      { name: 'SAP FICO', icon: '💰' },
      { name: 'SAP MM / WM', icon: '📦' },
      { name: 'SAP SD / LE', icon: '🚚' },
    ]
  },
  {
    title: 'Supply Chain & HR',
    items: [
      { name: 'SAP PP / QM', icon: '🏭' },
      { name: 'SAP HCM / SuccessFactors', icon: '👥' },
      { name: 'SAP Ariba', icon: '📋' },
      { name: 'SAP IBP / APO', icon: '📊' },
    ]
  },
  {
    title: 'Technical & Security',
    items: [
      { name: 'SAP BW / BW/4HANA', icon: '📈' },
      { name: 'SAP Basis / Netweaver', icon: '🖥️' },
      { name: 'SAP ABAP Development', icon: '💻' },
      { name: 'SAP GRC / Security', icon: '🔒' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Deep SAP Domain Expertise',
    text: 'Our recruiters are SAP-trained professionals, not generalists. We understand the difference between an SAP FICO consultant and an S/4HANA Finance expert — ensuring every candidate we send is genuinely qualified for your specific module and version.',
  },
  {
    icon: '✔',
    title: 'Rigorous Multi-Layer Vetting',
    text: 'Every SAP consultant undergoes verification of: SAP certification credentials, hands-on implementation experience (ECC/S/4HANA), industry domain knowledge (manufacturing, retail, BFSI, healthcare), and communication & stakeholder management skills.',
  },
  {
    icon: '⚙',
    title: 'Flexible SAP Engagement Models',
    text: 'We tailor our staffing solutions to your exact project needs — contract, contract-to-hire, or full-time. Whether it\'s a single SAP Basis administrator or a 20-member rollout team, we scale with you.',
  },
  {
    icon: '🛡',
    title: 'Risk-Reduced Hiring',
    text: 'Our deep SAP-specific screening reduces mis-hire risk significantly. We stand behind our placements with replacement guarantees, giving you peace of mind when it matters most.',
  },
];

const problemItems = [
  'Prolonged onboarding of consultants who lack domain-specific SAP expertise',
  'Critical SAP go-live deadlines missed — damaging stakeholder confidence',
  'Internal teams overburdened with firefighting instead of strategic delivery',
  'Budget overruns from rework, replacements, and project delays',
  'Delayed digital transformation roadmaps and competitive disadvantage',
];

const solutionPoints = [
  'SAP-only focus — every consultant screened against real-world project requirements',
  'Recruiters who understand the nuances of SAP implementations and modules',
  'Candidates matched precisely to your technical environment and industry vertical',
];

const outcomes = [
  {
    title: 'On-time SAP project delivery',
    text: 'Meet go-live milestones with confidence, backed by consultants who have done it before.',
  },
  {
    title: 'Reduced total cost of SAP ownership',
    text: 'Fewer mis-hires, faster onboarding, and lower rework costs.',
  },
  {
    title: 'Empowered internal teams',
    text: 'Let your in-house staff focus on strategic SAP governance rather than operational firefighting.',
  },
  {
    title: 'Scalable SAP talent pipeline',
    text: 'Access a ready bench of pre-vetted SAP professionals aligned to your growth roadmap.',
  },
  {
    title: 'Accelerated digital transformation',
    text: 'Move your S/4HANA migration, cloud adoption, or SAP upgrade forward without talent bottlenecks.',
  },
];

const SAPStaffAugHome = () => {
  return (
    <div>
      <Helmet>
        <title>SAP Staffing Solutions India | S/4HANA Recruitment | Kapil Technologies</title>
        <meta name="description" content="Kapil Technologies offers comprehensive SAP staffing solutions in India. Hire certified S/4HANA consultants, ECC experts, and SAP professionals for your implementation projects." />
        <meta name="keywords" content="SAP staffing India, S/4HANA recruitment India, SAP consultants India, ECC staffing India, SAP implementation India, SAP FI/CO consultants, SAP SD MM experts, SAP ABAP developers, SAP project staffing, SAP migration India, SAP basis consultants, SAP functional consultants, SAP technical staffing, SAP recruitment agency, Kapil Technologies SAP" />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="SAP Staffing Solutions India | S/4HANA Recruitment | Kapil Technologies" />
        <meta property="og:description" content="Kapil Technologies offers comprehensive SAP staffing solutions in India. Hire certified S/4HANA consultants and SAP professionals." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SAP Staffing Solutions India | S/4HANA Recruitment" />
        <meta name="twitter:description" content="Comprehensive SAP staffing solutions in India. Hire certified S/4HANA consultants and SAP professionals." />
        <link rel="canonical" href="https://kapiltechnologies.com/staff-augmentation/sap" />
      </Helmet>

      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602843/Kapil_Tech_SAP_Expert_pxwqcw.mp4"
        titleWhite="Certified SAP Consultants."
        titleGradient="Faster Hiring. Zero Compromises."
      />

      <TechWhyChoose 
        title="Struggling to Find the Right SAP Talent?"
        description="Kapil Tech is a dedicated SAP staffing company connecting businesses with pre-vetted SAP consultants across S/4HANA, FICO, MM, SD, SuccessFactors, Ariba, BW/4HANA, and 30+ SAP modules."
        stats={[
          { value: "500+", label: "SAP Consultants Placed" },
          { value: "98%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_SAP_Experts.jpg?updatedAt=1781603013619"
        imageAlt="SAP Staffing by Kapil Tech"
      />

      <TechRoles 
        title="SAP Modules & Specializations We Staff"
        description="Our SAP talent network spans every major functional, technical, and industry-specific module."
        roleGroups={roleGroups}
      />

      <TechStaffingServices 
        title="Why Leading Enterprises Choose Kapil Tech for SAP Staffing"
        description="We combine deep SAP domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      <TechRecruitingAgency 
        title="Are SAP Staffing Challenges Slowing Your Projects?"
        description="In today's digital transformation landscape, the demand for certified SAP professionals far outpaces supply. Generic IT staffing agencies circulate unqualified resumes, waste your time with irrelevant profiles, and fail to understand the nuances of SAP implementations — costing your business valuable time and budget."
        problemTitle="The Real Cost of a Wrong SAP Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated SAP Staffing Partner"
        solutionDescription="Unlike generalist staffing firms, Kapil Tech operates exclusively in the SAP ecosystem. Our SAP-only focus means every consultant in our network is screened, assessed, and benchmarked against real-world SAP project requirements — so you get the right fit, fast."
        solutionPoints={solutionPoints}
        ctaText="Find Your SAP Talent Today"
      />

      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your SAP initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="SAP Experts?"
        description="Tell us your SAP staffing need today. Our team will respond within hours — not days. Pre-vetted SAP consultants, matched to your module, timeline, and budget."
      />
    </div>
  )
}

export default SAPStaffAugHome;
